import { z } from 'zod';
import { londonAt, londonParts } from './home';
import { containsBlockedWord } from './profile';

// Gov, as Lagos Life's 🏛️ Gov (read from its public client code on 29 October 2026), in London reality. Decided
// with Jerry on 29 October 2026, part 1 (v0.58): every week London elects a Mayor. Anyone 3 days into their life
// stands for £250 with a slogan; votes are cast in person at City Hall's polling station (The Crystal, Royal
// Victoria Dock) until Sunday 8pm; a tie goes to whoever reached the total first. The Mayor holds office for the
// week: one policy for all of London (changeable once a day), an announcement every player sees on the map
// (word-checked, reportable, the team can take it down), and £150 a London day. Part 2 (v0.59): the cabinet,
// campaign managers and London Assembly Members; part 3 (v0.60): parties and primaries. No player Prime
// Minister: the real one stays on the map (world.ts).

// ---------- the Mayor's policies ----------

// what each policy does (the scope of each was decided with Jerry; the size is content)
export const GOV_POLICY_IDS = ['fares', 'wages', 'festival', 'food', 'rent'] as const;
export type GovPolicyId = (typeof GOV_POLICY_IDS)[number];
// half-price TfL fares: the travel sheet's bus, DLR and Tube, and the cable car's rides (not cabs, trains or
// flights)
export const GOV_TFL_MODES = ['bus', 'dlr', 'tube'] as const;
export const GOV_TFL_PLACES = ['cablecar'] as const;

const icon = z.string().min(1).max(8);
export const GovPolicy = z.object({
  id: z.enum(GOV_POLICY_IDS),
  label: z.string().trim().min(2).max(40),
  icon,
  // what players are told it does
  effect: z.string().trim().min(2).max(120),
  // how much: fares, food and rent come down by it; wages and fun go up by it
  percent: z.number().int().min(1).max(90),
  // the Mayor can pick it
  visible: z.boolean(),
});
export type GovPolicy = z.infer<typeof GovPolicy>;

export const GovContent = z.object({
  visible: z.boolean(),
  // standing for Mayor, and the Mayor's pay each London day (pence)
  standFeePence: z.number().int().min(0).max(100_000_000),
  mayorPayPence: z.number().int().min(0).max(10_000_000),
  // a life votes or stands once it is this many days old
  ageDays: z.number().int().min(0).max(60),
  // the Mayor changes the policy at most this often
  policyHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  // after the team takes an announcement down, the Mayor waits this long to post another
  announceWaitHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  // reports from this many players take an announcement down until the team looks
  reportsToHide: z.number().int().min(1).max(50),
  policies: z.array(GovPolicy).max(GOV_POLICY_IDS.length),
});
export type GovContent = z.infer<typeof GovContent>;

const policy = (id: GovPolicyId, label: string, icon: string, effect: string, percent: number): GovPolicy => ({
  id,
  label,
  icon,
  effect,
  percent,
  visible: true,
});
export const DEFAULT_GOV: GovContent = {
  visible: true,
  standFeePence: 25_000,
  mayorPayPence: 15_000,
  ageDays: 3,
  policyHours: 24,
  announceWaitHours: 24,
  reportsToHide: 3,
  policies: [
    policy('fares', 'Half-price TfL fares', '🚇', 'Bus, DLR, Tube and cable car fares are half price', 50),
    policy('wages', 'London Living Wage raise', '💼', 'Everyone earns 15% more per shift', 15),
    policy('festival', 'Festival season', '🎉', 'Fun at places around London is 30% stronger', 30),
    policy('food', 'Food support', '🥪', 'Food and groceries are 30% cheaper', 30),
    policy('rent', 'Rent support', '🏠', 'Rent is 15% lower', 15),
  ],
};
export const govOf = (content: { gov?: GovContent }) => content.gov ?? DEFAULT_GOV;
export function govProblems(gov: GovContent): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const item of gov.policies) {
    if (seen.has(item.id)) problems.push(`Gov: two policies are both "${item.id}".`);
    seen.add(item.id);
  }
  return problems;
}

// what the policy in force does to prices, pay and fun (1 when it does nothing to that)
export type GovEffects = { fares: number; wages: number; fun: number; food: number; rent: number };
export const NO_GOV_EFFECTS: GovEffects = { fares: 1, wages: 1, fun: 1, food: 1, rent: 1 };
export function govEffects(gov: GovContent, policyId: GovPolicyId | null | undefined): GovEffects {
  const found = policyId ? gov.policies.find((item) => item.id === policyId) : undefined;
  if (!found) return NO_GOV_EFFECTS;
  const down = 1 - found.percent / 100;
  const up = 1 + found.percent / 100;
  switch (found.id) {
    case 'fares':
      return { ...NO_GOV_EFFECTS, fares: down };
    case 'wages':
      return { ...NO_GOV_EFFECTS, wages: up };
    case 'festival':
      return { ...NO_GOV_EFFECTS, fun: up };
    case 'food':
      return { ...NO_GOV_EFFECTS, food: down };
    case 'rent':
      return { ...NO_GOV_EFFECTS, rent: down };
  }
}
// a price or pay with a policy's factor: whole pence, never below 1p when it was something
export const govPence = (pence: number, factor: number) =>
  factor === 1 || pence <= 0 ? pence : Math.max(1, Math.round(pence * factor));
// food: something paid for that feeds you (it raises hunger)
export const isFood = (thing: { pricePence?: number; effects?: { hunger?: number } }) =>
  Boolean(thing.pricePence) && (thing.effects?.hunger ?? 0) > 0;
// a TfL journey or ride the fares policy halves
export const isTflMode = (mode: string) => (GOV_TFL_MODES as readonly string[]).includes(mode);
export const isTflPlace = (placeId: string) => (GOV_TFL_PLACES as readonly string[]).includes(placeId);
// an activity under the policy in force (the server charges and pays this; the screens show it): food and the
// cable car's rides cost less, a shift pays more, fun away from home is stronger
type PricedThing = { placeId: string; pricePence?: number; wagePence?: number; effects?: { hunger?: number } };
export const govPriceOf = (activity: PricedThing, effects: GovEffects, pence = activity.pricePence ?? 0) =>
  govPence(pence, (isFood(activity) ? effects.food : 1) * (isTflPlace(activity.placeId) ? effects.fares : 1));
export const govWageOf = (pence: number, effects: GovEffects) => govPence(pence, effects.wages);
export const govFunAt = (placeId: string, effects: GovEffects) => (placeId === 'home' ? 1 : effects.fun);
// a fare on the travel sheet
export const govFareOf = (mode: string, pence: number, effects: GovEffects) =>
  isTflMode(mode) ? govPence(pence, effects.fares) : pence;

// ---------- the week ----------

// votes are cast in person here: City Hall's polling station (content edition 8)
export const GOV_POLLING_PLACE = 'cityhall';

// polls close every Sunday at 8pm, London time
export const POLLS_CLOSE = { weekday: 0, hour: 20 } as const;
// the next time polls close, strictly after `from`
export function nextPollsClose(from: Date): Date {
  const now = londonParts(from);
  for (let ahead = 0; ahead <= 7; ahead++) {
    const day = londonParts(new Date(Date.UTC(now.year, now.month - 1, now.day + ahead, 12)));
    if (day.weekday !== POLLS_CLOSE.weekday) continue;
    const at = londonAt(day.year, day.month, day.day, POLLS_CLOSE.hour);
    if (at > from) return at;
  }
  // a Sunday after 8pm: the one a week on
  const later = londonParts(new Date(Date.UTC(now.year, now.month - 1, now.day + 7, 12)));
  return londonAt(later.year, later.month, later.day, POLLS_CLOSE.hour);
}
// when a life may vote and stand (its first `ageDays` days are too new)
export const govOpensFor = (lifeStartedAt: Date, ageDays: number) =>
  new Date(lifeStartedAt.getTime() + ageDays * 86_400_000);
// a tie goes to whoever reached the total first: the candidate whose last counted vote came earliest
export function govWinner<T extends { votes: number; lastVoteAt: string | null; standingAt: string }>(
  candidates: T[],
): T | null {
  const counted = candidates.filter((item) => item.votes > 0);
  if (!counted.length) return null;
  return [...counted].sort(
    (a, b) =>
      b.votes - a.votes ||
      (a.lastVoteAt ?? '').localeCompare(b.lastVoteAt ?? '') ||
      a.standingAt.localeCompare(b.standingAt),
  )[0]!;
}

// ---------- what players write ----------

export const GOV_SLOGAN_MAX = 80;
export const GOV_ANNOUNCE_MAX = 140;
const words = (min: number, max: number) =>
  z
    .string()
    .trim()
    .min(min, `Say a bit more (${min} characters at least)`)
    .max(max, `Keep it to ${max} characters`)
    .refine((text) => !containsBlockedWord(text), 'Please say it another way');
export const GovStandRequest = z.object({ slogan: words(3, GOV_SLOGAN_MAX) }).strict();
export type GovStandRequest = z.infer<typeof GovStandRequest>;
export const GovVoteRequest = z.object({ candidateId: z.string().uuid() }).strict();
export type GovVoteRequest = z.infer<typeof GovVoteRequest>;
export const GovPolicyRequest = z.object({ policy: z.enum(GOV_POLICY_IDS) }).strict();
export type GovPolicyRequest = z.infer<typeof GovPolicyRequest>;
export const GovAnnounceRequest = z.object({ text: words(3, GOV_ANNOUNCE_MAX) }).strict();
export type GovAnnounceRequest = z.infer<typeof GovAnnounceRequest>;
export const GOV_REPORT_REASONS = {
  abuse: 'Abuse or hate',
  spam: 'Spam or adverts',
  personal: "Someone's personal details",
  other: 'Something else',
} as const;
export type GovReportReason = keyof typeof GOV_REPORT_REASONS;
export const GovReportRequest = z
  .object({
    announcementId: z.string().uuid(),
    reason: z.enum(Object.keys(GOV_REPORT_REASONS) as [GovReportReason, ...GovReportReason[]]),
  })
  .strict();
export type GovReportRequest = z.infer<typeof GovReportRequest>;

// ---------- over the wire ----------

export const GovPerson = z.object({ lifeId: z.string().uuid(), name: z.string() });
export type GovPerson = z.infer<typeof GovPerson>;
export const GovCandidateView = GovPerson.extend({
  id: z.string().uuid(),
  slogan: z.string(),
  votes: z.number().int(),
  standingAt: z.string(),
  lastVoteAt: z.string().nullable(),
});
export type GovCandidateView = z.infer<typeof GovCandidateView>;
export const GovAnnouncementView = z.object({ id: z.string().uuid(), text: z.string(), at: z.string() });
export type GovAnnouncementView = z.infer<typeof GovAnnouncementView>;
// the Mayor this week: who, until when, the policy in force and the announcement up
export const GovMayorView = GovPerson.extend({
  since: z.string(),
  until: z.string(),
  policy: z.enum(GOV_POLICY_IDS).nullable(),
  policyAt: z.string().nullable(),
  announcement: GovAnnouncementView.nullable(),
});
export type GovMayorView = z.infer<typeof GovMayorView>;
// what everyone sees on the map: the Mayor, the policy in force and the announcement
export const GovBanner = z.object({
  mayor: GovMayorView.nullable(),
  policy: GovPolicy.nullable(),
});
export type GovBanner = z.infer<typeof GovBanner>;
export const GovBannerReply = z.object({ banner: GovBanner });
export type GovBannerReply = z.infer<typeof GovBannerReply>;
// a report: the banner as it is now (hidden once enough players report it), and a thank-you
export const GovReportReply = GovBannerReply.extend({ message: z.string() });
export type GovReportReply = z.infer<typeof GovReportReply>;
// the result of last week's election
export const GovResultView = z.object({
  closedAt: z.string(),
  winner: GovPerson.extend({ votes: z.number().int() }).nullable(),
  // the winner reached the same total as someone else first
  tie: z.boolean(),
  total: z.number().int(),
});
export type GovResultView = z.infer<typeof GovResultView>;
export const GovElectionView = z.object({
  id: z.string().uuid(),
  closes: z.string(),
  paused: z.boolean(),
  candidates: z.array(GovCandidateView),
  total: z.number().int(),
  myVote: z.string().uuid().nullable(),
  mine: z.string().uuid().nullable(),
  // when a too-new life may vote and stand
  opensForMe: z.string().nullable(),
  // where votes are cast, and whether the player is there
  pollingPlace: z.object({ placeId: z.string(), name: z.string() }),
  atPolls: z.boolean(),
});
export type GovElectionView = z.infer<typeof GovElectionView>;
export const GovReply = z.object({
  visible: z.boolean(),
  mayor: GovMayorView.nullable(),
  isMayor: z.boolean(),
  // the Mayor may change the policy again from (null: now)
  policyFrom: z.string().nullable(),
  // the Mayor may post an announcement again from (null: now)
  announceFrom: z.string().nullable(),
  policies: z.array(GovPolicy),
  election: GovElectionView,
  last: GovResultView.nullable(),
  standFeePence: z.number().int(),
  mayorPayPence: z.number().int(),
  message: z.string().optional(),
});
export type GovReply = z.infer<typeof GovReply>;

// ---------- the team ----------

export const GovReportView = z.object({
  reason: z.enum(Object.keys(GOV_REPORT_REASONS) as [GovReportReason, ...GovReportReason[]]),
  by: z.string(),
  at: z.string(),
});
export const GovTeamAnnouncement = GovAnnouncementView.extend({
  mayor: GovPerson,
  status: z.enum(['up', 'hidden', 'taken_down', 'replaced']),
  reports: z.array(GovReportView),
});
export type GovTeamAnnouncement = z.infer<typeof GovTeamAnnouncement>;
export const GovTeamReply = z.object({
  election: z.object({
    id: z.string().uuid(),
    closes: z.string(),
    paused: z.boolean(),
    candidates: z.array(GovCandidateView),
    total: z.number().int(),
  }),
  mayor: GovMayorView.nullable(),
  announcements: z.array(GovTeamAnnouncement),
  results: z.array(GovResultView),
  message: z.string().optional(),
});
export type GovTeamReply = z.infer<typeof GovTeamReply>;
export const GovTeamAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('pause'), reason: z.string().trim().min(3).max(300) }).strict(),
  z.object({ action: z.literal('resume'), reason: z.string().trim().min(3).max(300) }).strict(),
  z
    .object({
      action: z.literal('take_down'),
      announcementId: z.string().uuid(),
      reason: z.string().trim().min(3).max(300),
    })
    .strict(),
  z
    .object({
      action: z.literal('restore'),
      announcementId: z.string().uuid(),
      reason: z.string().trim().min(3).max(300),
    })
    .strict(),
]);
export type GovTeamAction = z.infer<typeof GovTeamAction>;
